import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { IconTaskClappingHands } from "src/constants/icons";
import { APP_BREAKPOINT } from "src/constants/layout";
import { OpenBrowser } from "src/hybrid/components/Browser";
import translate from "src/lib/translate";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import type { TaskResponseKnown } from "src/state/TaskManagementBloc/TaskManagementBloc";
import AsyncContent from "src/ui/components/AsyncContent/AsyncContent";
import Collection from "src/ui/components/StyledComponents/Collection";
import CollectionTitle from "src/ui/components/StyledComponents/CollectionTitle";
import Translate from "src/ui/components/Translate/Translate";
import TaskListBloc from "./TaskListBloc";
import TaskListItem from "./TaskListItem";
import { TasksContext } from "src/constants/tasksContext";

//#region Styled Components
const EmptyStateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1.5rem;
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: var(--color-cream-darker);

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    gap: 0.75rem;
    padding: 1.5rem;
    margin: 0 0 2.5rem;
  }
`;

const EmptyStateIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2.75rem;
  aspect-ratio: 1;
  border: 1px solid var(--color-cream);
  border-radius: 0.5rem;
  box-sizing: border-box;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    width: 3.375rem;
  }
`;

//#endregion
interface TaskListProps {
  programs: KnownProgram[];
  showBadges?: boolean;
  filterVisibleTasks?: (task: TaskResponseKnown) => boolean;
  consolidateOnboardingTasks?: boolean;
  onTaskCompletionBehavior?: import("./TaskListBloc").TaskCompletionBehavior;
}

const TaskList: FC<TaskListProps> = ({
  programs,
  showBadges,
  filterVisibleTasks,
  consolidateOnboardingTasks,
  onTaskCompletionBehavior
}) => {
  const { setTerminalTasks, setTotalTasks } = React.useContext(TasksContext);

  const navigate = useNavigate();

  const openExternalUrl = async (url: string): Promise<void> => {
    await OpenBrowser(url, {
      useBaseUrl: false,
      presentationStyle: "popover"
    });
  };

  const [{ loading, displayTasks }, { getTranslationKey, handleTaskClick }] =
    useBlocNext(TaskListBloc, {
      props: {
        programs,
        filterVisibleTasks,
        navigate,
        openExternalUrl,
        setTerminalTasks,
        setTotalTasks,
        onTaskCompletionBehavior,
        consolidatedTasks: consolidateOnboardingTasks
      },
      onMount: (bloc) => {
        void bloc.loadTasks({
          setLoading: true
        });
        bloc.addListenerForTaskChanges();
      }
    });

  const title = translate("program.tasks.title", {
    context: programs[0]
  });

  if (loading && displayTasks.length === 0)
    return <AsyncContent check={[false]} height="16rem" />;

  if (displayTasks.length === 0) {
    return (
      <>
        <CollectionTitle>{title}</CollectionTitle>

        <EmptyStateRow>
          <EmptyStateIconWrapper>
            <IconTaskClappingHands />
          </EmptyStateIconWrapper>
          <p className="strong color-c-80 m0">
            <Translate msg="program.tasks.completed" />
          </p>
        </EmptyStateRow>
      </>
    );
  }

  return (
    <>
      <CollectionTitle className="m-bottom-neg">{title}</CollectionTitle>

      <Collection slot="content">
        {displayTasks.map((task, index) => {
          return (
            <TaskListItem
              key={
                task.id ?? `${task.program}/${task.group}/${task.slug}-${index}`
              }
              getTranslationKey={getTranslationKey}
              handleTaskClick={handleTaskClick}
              task={task}
              showBadge={showBadges}
            />
          );
        })}
      </Collection>
    </>
  );
};

export default TaskList;
